import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import { alpha } from '@mui/material/styles';

import Nav from './nav';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';

import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';

const FAIL_RED = '#C62828';
const WARNING_AMBER = '#D97706';
const SLATE_TEXT = '#607086';

const getGrade = (score) => {
  if (score == null || score === 'N/A') return '?';
  const numericScore = Number(score);
  if (Number.isNaN(numericScore)) return '?';
  if (numericScore < 60) return 'F';
  if (numericScore < 70) return 'D';
  if (numericScore < 80) return 'C';
  if (numericScore < 90) return 'B';
  if (numericScore <= 95) return 'A-';
  return 'A+';
};

const gradePalette = {
  'A+': PRIMARYCOLOR,
  'A-': PRIMARYCOLOR,
  B: '#1976D2',
  C: WARNING_AMBER,
  D: '#EF6C00',
  F: FAIL_RED,
  '?': '#94A3B8'
};

const gradeMessages = {
  'A+': 'Leak activity is currently limited and no material exposure trend is apparent.',
  'A-': 'Overall posture is strong, with only a modest amount of exposed data present.',
  B: 'Exposure is present but still reasonably contained compared with higher-risk engagements.',
  C: 'Several exposures are active and should be reviewed with priority.',
  D: 'Important dark web exposure exists and increases the likelihood of downstream abuse.',
  F: 'Material exposure is present and requires immediate credential and breach-response action.',
  '?': 'Risk score is not currently available for this monitoring engagement.'
};

const shellCardSx = {
  height: '100%',
  borderRadius: 4,
  border: '1px solid',
  borderColor: alpha(PRIMARYCOLOR, 0.1),
  boxShadow: `0 14px 28px ${alpha(PRIMARYCOLOR, 0.06)}`,
  backgroundColor: '#ffffff'
};

const formatNumber = (value) => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) return String(value ?? 0);
  return numericValue.toLocaleString();
};

const truncate = (value, max = 32) => {
  const text = String(value || '');
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}...`;
};

const formatPercent = (value) => `${Math.round(value)}%`;

const MetricCard = ({ label, value, helper, Icon, accent = PRIMARYCOLOR }) => (
  <Card sx={{ ...shellCardSx, borderTop: `3px solid ${accent}` }}>
    <CardContent sx={{ p: 2.2 }}>
      <Stack direction="row" spacing={1.4} alignItems="flex-start">
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 3,
            bgcolor: alpha(accent, 0.12),
            color: accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <Icon />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
            {label}
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.45, fontWeight: 800, color: '#10233B', lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.85, color: SLATE_TEXT, lineHeight: 1.65 }}>
            {helper}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const ExposureRow = ({ label, value, progress, accent }) => (
  <Box sx={{ py: 1.1 }}>
    <Stack direction="row" justifyContent="space-between" spacing={1.5} alignItems="center">
      <Typography variant="body2" sx={{ fontWeight: 700, color: '#20344B' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 800, color: '#10233B' }}>
        {formatNumber(value)}
      </Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={progress}
      sx={{
        mt: 0.9,
        height: 8,
        borderRadius: 999,
        backgroundColor: alpha(accent, 0.12),
        '& .MuiLinearProgress-bar': {
          borderRadius: 999,
          backgroundColor: accent
        }
      }}
    />
  </Box>
);

const DarkWebDashboard = () => {
  const { pentest_id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      const token = sessionStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      try {
        const response = await fetch(`${BASEURL}/pentests/darkweb/stats?pentest_id=${pentest_id}`, {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        if (data?.error) throw new Error(data.message || 'Failed to load stats');
        setStats(data?.stats || null);
      } catch (error) {
        console.error(error);
        setStats(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, pentest_id]);

  const derived = useMemo(() => {
    const currentStats = stats || {};
    const totalLeaks = Number(currentStats.total_leaks) || 0;
    const totalEmails = Number(currentStats.total_emails) || 0;
    const clearPasswords = Number(currentStats.clear_text_passwords) || 0;
    const hashedPasswords = Number(currentStats.hashed_passwords) || 0;
    const totalVin = Number(currentStats.total_vin) || 0;
    const totalName = Number(currentStats.total_name) || 0;
    const totalPhone = Number(currentStats.total_phone) || 0;

    const dbEntries = Object.entries(currentStats.database_leaks || {})
      .map(([name, count]) => ({ name, count: Number(count) || 0 }))
      .filter((row) => row.count > 0)
      .sort((left, right) => right.count - left.count);

    const topSource = dbEntries[0] || null;
    const sourceCount = dbEntries.length;
    const credentialTotal = clearPasswords + hashedPasswords;
    const topSourceShare = totalLeaks ? ((topSource?.count || 0) / totalLeaks) * 100 : 0;
    const clearPasswordShare = credentialTotal ? (clearPasswords / credentialTotal) * 100 : 0;
    const avgLeaksPerEmail = totalEmails ? totalLeaks / totalEmails : 0;

    const exposureMix = [
      { label: 'Cleartext Passwords', value: clearPasswords, accent: FAIL_RED },
      { label: 'Hashed Passwords', value: hashedPasswords, accent: WARNING_AMBER },
      { label: 'Phone Records', value: totalPhone, accent: PRIMARYCOLOR },
      { label: 'Identity Records', value: totalName, accent: '#2563EB' },
      { label: 'Vehicle Records', value: totalVin, accent: '#7C3AED' }
    ];

    const maxExposure = Math.max(...exposureMix.map((item) => item.value), 1);

    const narrative = totalLeaks === 0
      ? 'No dark web leak rows are currently associated with this monitoring engagement.'
      : clearPasswords > 0
        ? `${formatNumber(clearPasswords)} cleartext password entries were identified across ${formatNumber(sourceCount)} breach sources, which materially increases the likelihood of credential abuse.`
        : `${formatNumber(totalLeaks)} leak rows were identified across ${formatNumber(sourceCount)} breach sources. Even without cleartext passwords, the exposed data still expands the organization’s external risk footprint.`;

    return {
      totalLeaks,
      totalEmails,
      clearPasswords,
      hashedPasswords,
      totalVin,
      totalName,
      totalPhone,
      dbEntries,
      topSource,
      sourceCount,
      credentialTotal,
      topSourceShare,
      clearPasswordShare,
      avgLeaksPerEmail,
      exposureMix,
      maxExposure,
      narrative
    };
  }, [stats]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#F5F8FC' }}>
        <Nav />
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography sx={{ color: SLATE_TEXT }}>Loading dark web monitoring dashboard...</Typography>
        </Box>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#F5F8FC' }}>
        <Nav />
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Card sx={{ ...shellCardSx, maxWidth: 520 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#10233B' }}>
                Dark web statistics are unavailable
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: SLATE_TEXT, lineHeight: 1.75 }}>
                The monitoring stats for this engagement could not be loaded right now. Try refreshing the page or reopening the engagement.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  const grade = getGrade(stats.risk_score);
  const gradeColor = gradePalette[grade] || '#94A3B8';
  const gradeMessage = gradeMessages[grade] || '';
  const chartRows = derived.dbEntries.slice(0, 7);

  const sourceChartData = {
    labels: chartRows.map((row) => truncate(row.name, 28)),
    datasets: [
      {
        label: 'Leak Count',
        data: chartRows.map((row) => row.count),
        borderWidth: 0,
        borderRadius: 16,
        maxBarThickness: 20,
        backgroundColor: chartRows.map((_, index) => (index === 0 ? PRIMARYCOLOR : alpha(PRIMARYCOLOR, 0.65)))
      }
    ]
  };

  const sourceChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const item = chartRows[items[0]?.dataIndex || 0];
            return item?.name || '';
          },
          label: (context) => `${formatNumber(context.raw)} leak rows`
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        border: { display: false },
        grid: { color: alpha(PRIMARYCOLOR, 0.08) },
        ticks: { precision: 0, color: SLATE_TEXT }
      },
      y: {
        border: { display: false },
        grid: { display: false },
        ticks: { color: '#10233B', font: { weight: 700 } }
      }
    }
  };

  const metricCards = [
    {
      label: 'Total Leak Rows',
      value: formatNumber(derived.totalLeaks),
      helper: 'All leak records currently associated with this monitored organization.',
      Icon: StorageIcon,
      accent: PRIMARYCOLOR
    },
    {
      label: 'Emails Exposed',
      value: formatNumber(derived.totalEmails),
      helper: 'Unique email exposure count represented in the current dark web totals.',
      Icon: EmailIcon,
      accent: PRIMARYCOLOR
    },
    {
      label: 'Cleartext Passwords',
      value: formatNumber(derived.clearPasswords),
      helper: 'Highest-risk credential exposures where the password is directly usable.',
      Icon: LockIcon,
      accent: FAIL_RED
    },
    {
      label: 'Hashed Passwords',
      value: formatNumber(derived.hashedPasswords),
      helper: 'Credential exposures that still indicate historical compromise.',
      Icon: VpnKeyIcon,
      accent: WARNING_AMBER
    },
    {
      label: 'Phone Records',
      value: formatNumber(derived.totalPhone),
      helper: 'Phone-related data points linked to dark web leak records.',
      Icon: PhoneIcon,
      accent: '#2563EB'
    },
    {
      label: 'Identity Records',
      value: formatNumber(derived.totalName),
      helper: 'Name-based records that expand social engineering and impersonation risk.',
      Icon: PersonIcon,
      accent: '#0F766E'
    },
    {
      label: 'Vehicle Records',
      value: formatNumber(derived.totalVin),
      helper: 'VIN-related exposure present in the current leak inventory.',
      Icon: DirectionsCarIcon,
      accent: '#7C3AED'
    }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#F5F8FC' }}>
      <Nav />
      <Box sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Box>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
              Exposure Monitoring
            </Typography>
            <Typography variant="h4" sx={{ mt: 0.8, fontWeight: 800, color: '#10233B' }}>
              Dark Web Monitoring Dashboard
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.95, maxWidth: 820, color: SLATE_TEXT, lineHeight: 1.8 }}>
              Track breach-source concentration, credential exposure, and the overall severity of data surfaced across monitored dark web leak sources.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Chip label={`${formatNumber(derived.sourceCount)} breach source${derived.sourceCount === 1 ? '' : 's'}`} sx={{ fontWeight: 800, bgcolor: alpha(PRIMARYCOLOR, 0.08), color: PRIMARYCOLOR }} />
            {stats?.risk_letter_grade ? (
              <Chip label={`Server Grade ${stats.risk_letter_grade}`} sx={{ fontWeight: 800, bgcolor: alpha(gradeColor, 0.08), color: gradeColor }} />
            ) : null}
          </Stack>
        </Box>

        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} md={4} lg={3}>
            <Card sx={{ ...shellCardSx, height: '100%', borderTop: `4px solid ${gradeColor}` }}>
              <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
                  Risk Posture
                </Typography>
                <Typography variant="h1" sx={{ mt: 1.5, fontSize: { xs: '4.5rem', sm: '5.3rem' }, fontWeight: 900, lineHeight: 1, color: gradeColor }}>
                  {grade}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1.1, fontWeight: 800, color: '#10233B' }}>
                  Dark web exposure grade
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: SLATE_TEXT, lineHeight: 1.8 }}>
                  {gradeMessage}
                </Typography>

                <Box sx={{ mt: 2.2, pt: 0.35 }}>
                  <Stack direction="row" spacing={1.1} alignItems="center">
                    <Box sx={{ width: 42, height: 42, borderRadius: 3, bgcolor: alpha(gradeColor, 0.12), color: gradeColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <SecurityIcon />
                    </Box>
                    <Typography variant="body2" sx={{ color: SLATE_TEXT, lineHeight: 1.7 }}>
                      {stats?.risk_score != null ? `${formatNumber(stats.risk_score)} / 100 risk score recorded for this monitoring engagement.` : 'A numeric risk score is not currently available.'}
                    </Typography>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8} lg={9}>
            <Card sx={shellCardSx}>
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
                      Exposure Overview
                    </Typography>
                    <Typography variant="h5" sx={{ mt: 0.75, fontWeight: 800, color: '#10233B' }}>
                      High-level summary of current dark web exposure
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.85, maxWidth: 760, color: SLATE_TEXT, lineHeight: 1.8 }}>
                      {derived.narrative}
                    </Typography>
                  </Box>
                  <Chip icon={<InsightsRoundedIcon />} label={derived.topSource ? `Most active source: ${truncate(derived.topSource.name, 28)}` : 'No active breach sources'} sx={{ fontWeight: 800, bgcolor: alpha(PRIMARYCOLOR, 0.08), color: PRIMARYCOLOR, px: 0.5 }} />
                </Stack>

                <Grid container spacing={2} sx={{ mt: 0.6 }}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 1.8, borderRadius: 3, border: '1px solid', borderColor: alpha(PRIMARYCOLOR, 0.12), bgcolor: '#ffffff', borderLeft: `4px solid ${PRIMARYCOLOR}` }}>
                      <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
                        Monitored Emails
                      </Typography>
                      <Typography variant="h4" sx={{ mt: 0.75, fontWeight: 800, color: '#10233B' }}>
                        {formatNumber(derived.totalEmails)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.65, color: SLATE_TEXT }}>
                        Average of {derived.totalEmails ? derived.avgLeaksPerEmail.toFixed(1) : '0.0'} leak rows per exposed email.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 1.8, borderRadius: 3, border: '1px solid', borderColor: alpha(FAIL_RED, 0.12), bgcolor: '#ffffff', borderLeft: `4px solid ${FAIL_RED}` }}>
                      <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: FAIL_RED }}>
                        Cleartext Credential Risk
                      </Typography>
                      <Typography variant="h4" sx={{ mt: 0.75, fontWeight: 800, color: '#10233B' }}>
                        {formatPercent(derived.clearPasswordShare)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.65, color: SLATE_TEXT }}>
                        Cleartext share across all exposed credential records.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 1.8, borderRadius: 3, border: '1px solid', borderColor: alpha(PRIMARYCOLOR, 0.12), bgcolor: '#ffffff', borderLeft: `4px solid ${PRIMARYCOLOR}` }}>
                      <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
                        Source Concentration
                      </Typography>
                      <Typography variant="h4" sx={{ mt: 0.75, fontWeight: 800, color: '#10233B' }}>
                        {formatPercent(derived.topSourceShare)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.65, color: SLATE_TEXT }}>
                        Share of leak rows associated with the single largest source.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2.35 }}>
                  <Stack direction="row" justifyContent="space-between" spacing={1.5} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#10233B' }}>
                      Cleartext credential share
                    </Typography>
                    <Typography variant="body2" sx={{ color: SLATE_TEXT }}>
                      {formatNumber(derived.clearPasswords)} of {formatNumber(derived.credentialTotal)} credential entries
                    </Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={derived.clearPasswordShare} sx={{ mt: 0.8, height: 9, borderRadius: 999, backgroundColor: alpha(FAIL_RED, 0.12), '& .MuiLinearProgress-bar': { borderRadius: 999, backgroundColor: FAIL_RED } }} />
                </Box>

                <Box sx={{ mt: 1.7 }}>
                  <Stack direction="row" justifyContent="space-between" spacing={1.5} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#10233B' }}>
                      Top source concentration
                    </Typography>
                    <Typography variant="body2" sx={{ color: SLATE_TEXT }}>
                      {derived.topSource ? `${truncate(derived.topSource.name, 36)} (${formatNumber(derived.topSource.count)})` : 'No source available'}
                    </Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={derived.topSourceShare} sx={{ mt: 0.8, height: 9, borderRadius: 999, backgroundColor: alpha(PRIMARYCOLOR, 0.12), '& .MuiLinearProgress-bar': { borderRadius: 999, backgroundColor: PRIMARYCOLOR } }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {metricCards.map((metric) => (
            <Grid item xs={12} sm={6} lg={3} key={metric.label}>
              <MetricCard {...metric} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} lg={7}>
            <Card sx={shellCardSx}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
                  Breach Source Activity
                </Typography>
                <Typography variant="h5" sx={{ mt: 0.75, fontWeight: 800, color: '#10233B' }}>
                  Highest-volume breach sources
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.85, color: SLATE_TEXT, lineHeight: 1.8 }}>
                  The chart below highlights the sources contributing the largest number of leak rows to this monitoring engagement.
                </Typography>

                {chartRows.length ? (
                  <Box sx={{ mt: 2.2, height: 320 }}>
                    <Bar data={sourceChartData} options={sourceChartOptions} />
                  </Box>
                ) : (
                  <Box sx={{ mt: 2, p: 2.2, borderRadius: 3, border: '1px dashed', borderColor: alpha(PRIMARYCOLOR, 0.16), bgcolor: '#ffffff' }}>
                    <Typography variant="body2" sx={{ color: SLATE_TEXT }}>
                      No breach-source totals are available yet.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Card sx={shellCardSx}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
                  Exposure Profile
                </Typography>
                <Typography variant="h5" sx={{ mt: 0.75, fontWeight: 800, color: '#10233B' }}>
                  What data is most exposed
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.85, color: SLATE_TEXT, lineHeight: 1.8 }}>
                  Compare the relative weight of credential, identity, and contact-related exposure across the current leak inventory.
                </Typography>

                <Box sx={{ mt: 1.8 }}>
                  {derived.exposureMix.map((item) => (
                    <ExposureRow
                      key={item.label}
                      label={item.label}
                      value={item.value}
                      accent={item.accent}
                      progress={derived.maxExposure ? (item.value / derived.maxExposure) * 100 : 0}
                    />
                  ))}
                </Box>

                <Divider sx={{ my: 1.4, borderColor: alpha(PRIMARYCOLOR, 0.1) }} />

                <Stack direction="row" spacing={1.3} alignItems="flex-start">
                  <Box sx={{ width: 44, height: 44, borderRadius: 3, bgcolor: alpha(PRIMARYCOLOR, 0.1), color: PRIMARYCOLOR, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <HubRoundedIcon />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, color: '#10233B' }}>
                      Analyst snapshot
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.7, color: SLATE_TEXT, lineHeight: 1.8 }}>
                      {derived.topSource
                        ? `${truncate(derived.topSource.name, 34)} is currently the dominant leak source, accounting for ${formatPercent(derived.topSourceShare)} of the observed leak rows.`
                        : 'No single breach source has been identified yet in the current monitoring totals.'}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card sx={shellCardSx}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
                Source Breakdown
              </Typography>
              <Typography variant="h5" sx={{ mt: 0.75, fontWeight: 800, color: '#10233B' }}>
                Ranked breach source inventory
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.85, color: SLATE_TEXT, lineHeight: 1.8 }}>
                Use this breakdown to see where the leak concentration is coming from before drilling into the full findings view.
              </Typography>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: PRIMARYCOLOR, color: '#ffffff', fontWeight: 800, letterSpacing: '0.03em', borderBottom: 'none' } }}>
                    <TableCell width={72}>Rank</TableCell>
                    <TableCell>Breach Source</TableCell>
                    <TableCell align="right">Leak Rows</TableCell>
                    <TableCell align="right">Share</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {derived.dbEntries.length ? (
                    derived.dbEntries.slice(0, 10).map((row, index) => (
                      <TableRow key={row.name} hover sx={{ '& td': { borderBottomColor: alpha(PRIMARYCOLOR, 0.06) }, '&:nth-of-type(odd)': { backgroundColor: alpha(PRIMARYCOLOR, 0.016) } }}>
                        <TableCell>
                          <Chip label={`#${index + 1}`} size="small" sx={{ fontWeight: 800, bgcolor: index === 0 ? alpha(PRIMARYCOLOR, 0.12) : alpha(PRIMARYCOLOR, 0.06), color: PRIMARYCOLOR }} />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1.1} alignItems="center">
                            <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(PRIMARYCOLOR, 0.1), color: PRIMARYCOLOR }}>
                              <PublicRoundedIcon sx={{ fontSize: 18 }} />
                            </Avatar>
                            <Typography sx={{ fontWeight: 700, color: '#20344B' }}>
                              {row.name}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: '#10233B' }}>
                          {formatNumber(row.count)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: SLATE_TEXT }}>
                          {formatPercent(derived.totalLeaks ? (row.count / derived.totalLeaks) * 100 : 0)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 7 }}>
                        <Typography sx={{ fontWeight: 700, color: '#10233B' }}>
                          No breach-source rows available.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.6, color: SLATE_TEXT }}>
                          Source ranking will appear once database leak totals are available.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default DarkWebDashboard;
